import { useGetLoansV2 } from "../fbs/fbs";
import { useGetV1UserLoans } from "../publizon/publizon";
import { useDigitalLoans } from "@danskernesdigitalebibliotek/dpl-service-layer";
import { calculateRoundedUpDaysUntil } from "./helpers/date";
import { materialIsOverdue } from "./helpers/general";
import {
  mapDigitalLoanToLoanType,
  mapFBSLoanToLoanType,
  mapPublizonLoanToLoanType
} from "./helpers/list-mapper";
import { LoanType } from "./types/loan-type";
import useBiblioAdapter from "./useBiblioAdapter";
import useLoanThresholds from "./useLoanThresholds";
import { isPatron } from "./helpers/user";

// Loans with more than warning-threshold days until due
const filterLoansNotOverdue = (loans: LoanType[], warning: number) => {
  return loans.filter(({ dueDate }) => {
    const due: string = dueDate || "";
    const daysUntilExpiration = calculateRoundedUpDaysUntil(due);
    return daysUntilExpiration - warning > 0;
  });
};
// Loans overdue
const filterLoansOverdue = (loans: LoanType[]) => {
  return loans.filter(({ dueDate }) => {
    return materialIsOverdue(dueDate);
  });
};
//
const filterLoansSoonOverdue = (loans: LoanType[], warning: number) => {
  return loans.filter(({ dueDate }) => {
    const due: string = dueDate || "";
    const daysUntilExpiration = calculateRoundedUpDaysUntil(due);
    return (
      daysUntilExpiration - warning <= 0 &&
      daysUntilExpiration - warning >= -warning
    );
  });
};

type Loans = {
  loans: LoanType[];
  overdue: LoanType[];
  soonOverdue: LoanType[];
  farFromOverdue: LoanType[];
  isLoading: boolean;
  isError: boolean;
};

export type UseLoansType = {
  all: Loans;
  fbs: Loans;
  // Digital loans as one list, whichever service issued them. Which provider
  // a loan came from does not change how it is rendered, and during the
  // transition a user has loans from both.
  digital: Loans;
};

type UseLoans = () => UseLoansType;

// useLoans is a custom hook that fetches loans from both FBS and the digital
// materials providers and combines them into lists. The loans are then
// divided into three categories: overdue, soon overdue, and far from overdue.
// The hook is NOT responsible for any sorting of the loans.
const useLoans: UseLoans = () => {
  const viaBiblioAdapter = useBiblioAdapter();
  // Loans belong to a patron, so they are only fetched with a user token.
  const userIsPatron = isPatron();
  const {
    data: loansFbs,
    isLoading: isLoadingFbs,
    isError: isErrorFbs
  } = useGetLoansV2({ query: { enabled: userIsPatron } });
  const {
    data: loansPublizon,
    isLoading: isLoadingPublizon,
    isError: isErrorPublizon
  } = useGetV1UserLoans(undefined, { query: { enabled: userIsPatron } });
  const {
    data: loansServiceLayer,
    isLoading: isLoadingServiceLayer,
    isError: isErrorServiceLayer
  } = useDigitalLoans({ enabled: viaBiblioAdapter && userIsPatron });

  const threshold = useLoanThresholds();
  // A disabled query is never loading or in error so the service layer states
  // only count when the feature flag has enabled the query.
  const isLoadingDigital = isLoadingPublizon || isLoadingServiceLayer;
  const isErrorDigital = isErrorPublizon || isErrorServiceLayer;
  const loansIsLoading = isLoadingFbs || isLoadingDigital;
  const loansIsError = isErrorFbs || isErrorDigital;

  // map loans to same type
  const mappedLoansFbs =
    loansFbs && Array.isArray(loansFbs) ? mapFBSLoanToLoanType(loansFbs) : [];
  const mappedLoansPublizon = loansPublizon?.loans
    ? mapPublizonLoanToLoanType(loansPublizon.loans)
        // TODO: is it necessary to filter out loans without dueDate?
        // there are loans without dueDate in the publizon MOCK data
        .filter((item) => item.dueDate)
    : [];
  const mappedLoansServiceLayer = loansServiceLayer?.loans
    ? mapDigitalLoanToLoanType(loansServiceLayer.loans)
    : [];
  const mappedLoansDigital = [
    ...mappedLoansPublizon,
    ...mappedLoansServiceLayer
  ];

  // Combine all loans from both FBS and the digital materials provider
  const loans = [...mappedLoansFbs, ...mappedLoansDigital];

  // Combine "overdue loans" from both FBS and the digital materials provider
  const loansOverdueFBS = filterLoansOverdue(mappedLoansFbs);
  const loansOverdueDigital = filterLoansOverdue(mappedLoansDigital);
  const loansOverdue = [...loansOverdueFBS, ...loansOverdueDigital];

  // combine "soon overdue" loans from both FBS and the digital materials
  // provider
  const loansSoonOverdueFBS = filterLoansSoonOverdue(
    mappedLoansFbs,
    threshold.warning
  );
  const loansSoonOverdueDigital = filterLoansSoonOverdue(
    mappedLoansDigital,
    threshold.warning
  );
  const loansSoonOverdue = [...loansSoonOverdueFBS, ...loansSoonOverdueDigital];

  // combine "far from overdue" loans from both FBS and the digital materials
  // provider
  const loansFarFromOverdueFBS = filterLoansNotOverdue(
    mappedLoansFbs,
    threshold.warning
  );
  const loansFarFromOverdueDigital = filterLoansNotOverdue(
    mappedLoansDigital,
    threshold.warning
  );
  const loansFarFromOverdue = [
    ...loansFarFromOverdueFBS,
    ...loansFarFromOverdueDigital
  ];

  return {
    all: {
      loans,
      overdue: loansOverdue,
      soonOverdue: loansSoonOverdue,
      farFromOverdue: loansFarFromOverdue,
      isLoading: loansIsLoading,
      isError: loansIsError
    },
    fbs: {
      loans: mappedLoansFbs,
      overdue: loansOverdueFBS,
      soonOverdue: loansSoonOverdueFBS,
      farFromOverdue: loansFarFromOverdueFBS,
      isLoading: isLoadingFbs,
      isError: isErrorFbs
    },
    digital: {
      loans: mappedLoansDigital,
      overdue: loansOverdueDigital,
      soonOverdue: loansSoonOverdueDigital,
      farFromOverdue: loansFarFromOverdueDigital,
      isLoading: isLoadingDigital,
      isError: isErrorDigital
    }
  };
};

export default useLoans;
